<template>
    <PageContainer>
        <MainCard>
            <TabNavigation>
                <TabButton :class="{ active: activeTab === 'account' }" @click="activeTab = 'account'">
                    <BankCircleOutline :size="25" :style="{ paddingTop: '3px' }" />
                    계좌 관리
                </TabButton>
                <TabButton :class="{ active: activeTab === 'card' }" @click="activeTab = 'card'">
                    <CreditCard :size="25" :style="{ paddingTop: '3px' }" />
                    카드 관리
                </TabButton>
            </TabNavigation>

            <!-- * 계좌 탭 내용 -->
            <div v-if="activeTab === 'account'">
                <SearchSection>
                    <SectionTitle> 입출금내역
                        <span v-if="bankTransactionFilter !== 'all'">
                            ({{ filteredBankTransactions.length }}건 / 전체 {{ bankTransactions.length }}건)
                        </span>
                        <span v-else>
                            ({{ bankTransactions.length }}건)
                        </span>
                    </SectionTitle>
                    <SearchForm @submit="handleSearchSubmit">
                        <SearchRow>
                            <SearchFormInput v-model="searchForm.name" placeholder="거래내용, 거래기록사항, 거래번호, 거래메모를 입력하세요" />
                            <SearchButton type=" submit" :disabled="!isValidSearch">
                                <Magnify :size="20" />
                            </SearchButton>
                            <ClaimListButton @click="loadBankTransactions">
                                <History :size="20" :style="{ paddingTop: '3px' }" />
                                입출금내역 조회
                            </ClaimListButton>
                            <ClaimListButton @click="GetBankAccountManagementURL">
                                <DockWindow :size="20" :style="{ paddingTop: '3px' }" />
                                계좌관리 팝업
                            </ClaimListButton>

                            <TransactionsActions>
                                <FilterSelect v-model="bankTransactionFilter"
                                    @change="handleBankTransactionFilterChange">
                                    <option value="all">전체</option>
                                    <option value="deposit">입금</option>
                                    <option value="withdrawal">출금</option>
                                </FilterSelect>

                                <FilterSelect v-model="quickDateSelect" @change="handleQuickDateSelect">
                                    <option value="">직접 선택</option>
                                    <option value="today">당일</option>
                                    <option value="yesterday">전일</option>
                                    <option value="3days">3일</option>
                                    <option value="1week">1주</option>
                                    <option value="15days">15일</option>
                                    <option value="1month">1개월</option>
                                    <option value="3months">3개월</option>
                                    <option value="6months">6개월</option>
                                </FilterSelect>

                                <DateSelectionGroup>
                                    <DateInput type="date" v-model="bankTransactionDateRange.startDate"
                                        :max="bankTransactionDateRange.endDate || today"
                                        @change="handleBankTransactionStartDateChange" />
                                    <DateSeparator>~</DateSeparator>
                                    <DateInput type="date" v-model="bankTransactionDateRange.endDate"
                                        :min="bankTransactionDateRange.startDate" :max="today"
                                        @change="handleBankTransactionEndDateChange" />
                                </DateSelectionGroup>
                            </TransactionsActions>
                        </SearchRow>
                    </SearchForm>
                </SearchSection>

                <!-- 로딩 스피너 -->
                <LoadingOverlay v-if="isLoadingBankTransactions">
                    <LoadingSpinner></LoadingSpinner>
                    <LoadingText>입출금내역을 불러오는 중...</LoadingText>
                </LoadingOverlay>

                <!-- * 입출금내역 테이블 -->
                <TransactionsTable
                    v-if="filteredBankTransactions && filteredBankTransactions.length > 0 && !isLoadingBankTransactions">
                    <TransactionsTableHeader>
                        <TransactionsTableRow>
                            <TransactionsTableHeaderCell>순번</TransactionsTableHeaderCell>
                            <TransactionsTableHeaderCell>거래일시</TransactionsTableHeaderCell>
                            <TransactionsTableHeaderCell>출금금액</TransactionsTableHeaderCell>
                            <TransactionsTableHeaderCell>입금금액</TransactionsTableHeaderCell>
                            <TransactionsTableHeaderCell>잔액</TransactionsTableHeaderCell>
                            <TransactionsTableHeaderCell>거래내용</TransactionsTableHeaderCell>
                            <TransactionsTableHeaderCell>거래기록사항</TransactionsTableHeaderCell>
                            <TransactionsTableHeaderCell>거래번호</TransactionsTableHeaderCell>
                            <TransactionsTableHeaderCell>거래메모</TransactionsTableHeaderCell>
                            <TransactionsTableHeaderCell>댓글</TransactionsTableHeaderCell>
                        </TransactionsTableRow>
                    </TransactionsTableHeader>
                    <TransactionsTableBody>
                        <TransactionsTableRow v-for="(transaction, index) in filteredBankTransactions" :key="index"
                            :class="{ 'blurred': isTestUser }">
                            <TransactionsTableCell>
                                <span style="font-weight: 600; color: #64748b;">{{ index + 1 }}</span>
                            </TransactionsTableCell>
                            <TransactionsTableCell>
                                <DateDisplay>{{ formatTransactionDateTime(transaction.transactionDate) }}
                                </DateDisplay>
                            </TransactionsTableCell>
                            <TransactionsTableCell>
                                <span v-if="transaction.withdrawalAmount > 0" style="color: #dc2626; font-weight: 600;">
                                    {{ formatPrice(transaction.withdrawalAmount) }}원
                                </span>
                                <span v-else>-</span>
                            </TransactionsTableCell>
                            <TransactionsTableCell>
                                <span v-if="transaction.depositAmount > 0" style="color: #2563eb; font-weight: 600;">
                                    {{ formatPrice(transaction.depositAmount) }}원
                                </span>
                                <span v-else>-</span>
                            </TransactionsTableCell>
                            <TransactionsTableCell>
                                <AmountDisplay>{{ formatPrice(transaction.balance) }}원</AmountDisplay>
                            </TransactionsTableCell>
                            <TransactionsTableCell>
                                <span style="font-weight: 500; color: #1f2937;">{{ transaction.transType ||
                                    '-' }}</span>
                            </TransactionsTableCell>
                            <TransactionsTableCell>
                                <span style="font-size: 13px; color: #1f2937;">
                                    {{ transaction.transRemark1 || '-' }}
                                </span>
                            </TransactionsTableCell>
                            <TransactionsTableCell>
                                <span style="font-size: 13px; color: #6b7280;">{{ transaction.transOffice ||
                                    '-' }}</span>
                            </TransactionsTableCell>
                            <TransactionsTableCell>
                                <span
                                    style="font-size: 13px; color: #6b7280; cursor: pointer; text-decoration: underline;"
                                    @click="editTransactionMemo(transaction)"
                                    :title="transaction.transactionMemo ? '클릭하여 메모 수정' : '클릭하여 메모 작성'">
                                    {{ transaction.transactionMemo || '메모 작성' }}
                                </span>
                            </TransactionsTableCell>
                            <TransactionsTableCell>
                                <span
                                    style="font-size: 13px; color: #6b7280; cursor: pointer; text-decoration: underline;"
                                    @click="editTransactionComment(transaction)"
                                    :title="transaction.comments && transaction.comments.length > 0 ? '클릭하여 댓글 수정/삭제' : '클릭하여 댓글 작성'">
                                    {{ getCommentDisplayText(transaction) }}
                                </span>
                            </TransactionsTableCell>
                        </TransactionsTableRow>
                    </TransactionsTableBody>
                </TransactionsTable>

                <EmptyTransactionState
                    v-else-if="filteredBankTransactions.length === 0 && bankTransactions.length > 0 && !isLoadingBankTransactions">
                    <EmptyIcon>🔍</EmptyIcon>
                    <EmptyTitle>필터링된 결과가 없습니다</EmptyTitle>
                    <EmptyDescription>선택한 필터 조건에 맞는 입출금내역이 없습니다. 다른 조건을 선택해보세요.</EmptyDescription>
                </EmptyTransactionState>

                <EmptyTransactionState v-else-if="bankTransactions.length === 0 && !isLoadingBankTransactions">
                    <EmptyIcon>💰</EmptyIcon>
                    <EmptyTitle>입출금내역이 없습니다</EmptyTitle>
                    <EmptyDescription>입출금내역 조회 버튼을 클릭하여 은행에서 데이터를 가져오세요</EmptyDescription>
                </EmptyTransactionState>
            </div>

            <!-- * 카드 탭 내용 -->
            <div v-if="activeTab === 'card'">
                <SearchSection>
                    <SectionTitle>카드 사용내역
                        <span v-if="cardTransactionFilter !== 'all'">
                            ({{ filteredCardTransactions.length }}건 / 전체 {{ cardTransactions.length }}건)
                        </span>
                        <span v-else>
                            ({{ cardTransactions.length }}건)
                        </span>
                    </SectionTitle>
                    <SearchForm @submit="handleCardSearchSubmit">
                        <SearchRow>
                            <SearchFormInput v-model="cardSearchForm.name" placeholder="카드명을 입력하세요" />
                            <SearchButton type="submit" :disabled="!isValidCardSearch">
                                <Magnify :size="20" />
                            </SearchButton>
                            <ClaimListButton @click="loadCardTransactions">
                                <History :size="20" :style="{ paddingTop: '3px' }" />
                                카드사용내역 조회
                            </ClaimListButton>
                            <ClaimListButton @click="GetCardManagementURL">
                                <DockWindow :size="20" :style="{ paddingTop: '3px' }" />
                                카드관리 팝업
                            </ClaimListButton>

                            <TransactionsActions>
                                <FilterSelect v-model="cardTransactionFilter"
                                    @change="handleCardTransactionFilterChange">
                                    <option value="all">전체</option>
                                    <option value="purchase">구매</option>
                                    <option value="refund">환불</option>
                                </FilterSelect>

                                <FilterSelect v-model="cardQuickDateSelect" @change="handleCardQuickDateSelect">
                                    <option value="">직접 선택</option>
                                    <option value="today">당일</option>
                                    <option value="yesterday">전일</option>
                                    <option value="3days">3일</option>
                                    <option value="1week">1주</option>
                                    <option value="15days">15일</option>
                                    <option value="1month">1개월</option>
                                    <option value="3months">3개월</option>
                                    <option value="6months">6개월</option>
                                </FilterSelect>

                                <DateSelectionGroup>
                                    <DateInput type="date" v-model="cardTransactionDateRange.startDate"
                                        :max="cardTransactionDateRange.endDate || today"
                                        @change="handleCardTransactionStartDateChange" />
                                    <DateSeparator>~</DateSeparator>
                                    <DateInput type="date" v-model="cardTransactionDateRange.endDate"
                                        :min="cardTransactionDateRange.startDate" :max="today"
                                        @change="handleCardTransactionEndDateChange" />
                                </DateSelectionGroup>
                            </TransactionsActions>
                        </SearchRow>
                    </SearchForm>
                </SearchSection>

                <!-- * 카드 사용내역 테이블 -->
                <TransactionsTable v-if="filteredCardTransactions && filteredCardTransactions.length > 0">
                    <TransactionsTableHeader>
                        <TransactionsTableRow>
                            <TransactionsTableHeaderCell>순번</TransactionsTableHeaderCell>
                            <TransactionsTableHeaderCell>사용일시</TransactionsTableHeaderCell>
                            <TransactionsTableHeaderCell>사용금액</TransactionsTableHeaderCell>
                            <TransactionsTableHeaderCell>할부개월</TransactionsTableHeaderCell>
                            <TransactionsTableHeaderCell>승인번호</TransactionsTableHeaderCell>
                            <TransactionsTableHeaderCell>가맹점명</TransactionsTableHeaderCell>
                            <TransactionsTableHeaderCell>카드종류</TransactionsTableHeaderCell>
                            <TransactionsTableHeaderCell>카드번호</TransactionsTableHeaderCell>
                            <TransactionsTableHeaderCell>메모</TransactionsTableHeaderCell>
                            <TransactionsTableHeaderCell>댓글</TransactionsTableHeaderCell>
                        </TransactionsTableRow>
                    </TransactionsTableHeader>
                    <TransactionsTableBody>
                        <TransactionsTableRow v-for="(transaction, index) in filteredCardTransactions" :key="index">
                            <TransactionsTableCell>
                                <span style="font-weight: 600; color: #64748b;">{{ index + 1 }}</span>
                            </TransactionsTableCell>
                            <TransactionsTableCell>
                                <DateDisplay>{{ formatCardTransactionDateTime(transaction.useDate) }}
                                </DateDisplay>
                            </TransactionsTableCell>
                            <TransactionsTableCell>
                                <span style="color: #dc2626; font-weight: 600;">
                                    {{ formatPrice(transaction.useAmount) }}원
                                </span>
                            </TransactionsTableCell>
                            <TransactionsTableCell>
                                <span style="font-weight: 500; color: #1f2937;">
                                    {{ transaction.installmentMonths || '일시불' }}
                                </span>
                            </TransactionsTableCell>
                            <TransactionsTableCell>
                                <span style="font-size: 13px; color: #6b7280;">
                                    {{ transaction.approvalNumber || '-' }}
                                </span>
                            </TransactionsTableCell>
                            <TransactionsTableCell>
                                <span style="font-size: 13px; color: #1f2937;">
                                    {{ transaction.merchantName || '-' }}
                                </span>
                            </TransactionsTableCell>
                            <TransactionsTableCell>
                                <span style="font-size: 13px; color: #6b7280;">
                                    {{ transaction.cardType || '-' }}
                                </span>
                            </TransactionsTableCell>
                            <TransactionsTableCell>
                                <span style="font-size: 13px; color: #6b7280;">
                                    {{ transaction.cardNumber || '-' }}
                                </span>
                            </TransactionsTableCell>
                            <TransactionsTableCell>
                                <span
                                    style="font-size: 13px; color: #6b7280; cursor: pointer; text-decoration: underline;"
                                    @click="editCardTransactionMemo(transaction)"
                                    :title="transaction.memo ? '클릭하여 메모 수정' : '클릭하여 메모 작성'">
                                    {{ transaction.memo || '메모 작성' }}
                                </span>
                            </TransactionsTableCell>
                            <TransactionsTableCell>
                                <span
                                    style="font-size: 13px; color: #6b7280; cursor: pointer; text-decoration: underline;"
                                    @click="editCardTransactionComment(transaction)"
                                    :title="transaction.comments && transaction.comments.length > 0 ? '클릭하여 댓글 수정/삭제' : '클릭하여 댓글 작성'">
                                    {{ getCardCommentDisplayText(transaction) }}
                                </span>
                            </TransactionsTableCell>
                        </TransactionsTableRow>
                    </TransactionsTableBody>
                </TransactionsTable>

                <EmptyTransactionState v-else-if="filteredCardTransactions.length === 0 && cardTransactions.length > 0">
                    <EmptyIcon>🔍</EmptyIcon>
                    <EmptyTitle>필터링된 결과가 없습니다</EmptyTitle>
                    <EmptyDescription>선택한 필터 조건에 맞는 카드 사용내역이 없습니다. 다른 조건을 선택해보세요.</EmptyDescription>
                </EmptyTransactionState>

                <EmptyTransactionState v-else-if="cardTransactions.length === 0">
                    <EmptyIcon>💳</EmptyIcon>
                    <EmptyTitle>카드 사용내역이 없습니다</EmptyTitle>
                    <EmptyDescription>카드 사용내역 조회 버튼을 클릭하여 카드사에서 데이터를 가져오세요</EmptyDescription>
                </EmptyTransactionState>
            </div>
        </MainCard>

        <!-- ! 댓글 모달 -->
        <PaymentCommentModal :is-visible="showCommentModal" :transaction="currentTransaction"
            @close="handleCommentModalClose" @save-comment="handleCommentSave" @delete-comment="handleCommentDelete"
            @add-comment="addNewComment" />
    </PageContainer>
</template>

<script>
import * as PaymentPageCss from '@/assets/styles/payment/PaymentPageCss.js';
import PaymentCommentModal from './modal/PaymentCommentModal.vue';

import Magnify from 'vue-material-design-icons/Magnify.vue';
import CreditCard from 'vue-material-design-icons/CreditCard.vue';
import Refresh from 'vue-material-design-icons/Refresh.vue';
import History from 'vue-material-design-icons/History.vue';
import DockWindow from 'vue-material-design-icons/DockWindow.vue';
import BankCircleOutline from 'vue-material-design-icons/BankCircleOutline.vue';

export default {
    name: 'PaymentPage',

    components: {
        ...PaymentPageCss,
        PaymentCommentModal,
        Magnify,
        CreditCard,
        Refresh,
        History,
        DockWindow,
        BankCircleOutline
    },

    data() {
        return {
            // 탭 상태
            activeTab: 'account',

            // 계좌 관련 데이터
            searchForm: {
                name: '',
                number: ''
            },
            bankTransactions: [],
            bankTransactionFilter: 'all',
            bankTransactionDateRange: {
                startDate: '',
                endDate: ''
            },
            quickDateSelect: '',
            isLoadingBankTransactions: false,

            // 카드 관련 데이터
            cardSearchForm: {
                name: ''
            },
            cardTransactions: [],
            cardTransactionFilter: 'all',
            cardTransactionDateRange: {
                startDate: '',
                endDate: ''
            },
            cardQuickDateSelect: '',

            // 댓글 모달 관련
            showCommentModal: false,
            currentTransaction: null,
        };
    },

    computed: {
        isTestUser() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                return userInfo && userInfo.id === 'test';
            } catch (error) {
                return false;
            }
        },
        today() {
            const today = new Date();
            return today.toISOString().split('T')[0];
        },
        isValidSearch() {
            return this.searchForm.name.trim() || this.searchForm.number.trim();
        },
        isValidCardSearch() {
            return this.cardSearchForm.name.trim();
        },
        // 입출금내역 필터링
        filteredBankTransactions() {
            let filtered = this.bankTransactions;

            // 검색어가 있는 경우 검색 결과만 사용
            if (this.searchForm.name.trim()) {
                const searchTerm = this.searchForm.name.trim().toLowerCase();
                filtered = filtered.filter(transaction => {
                    // 거래내용 검색
                    if (transaction.transRemark1 &&
                        transaction.transRemark1.toLowerCase().includes(searchTerm)) {
                        return true;
                    }

                    // 거래기록사항 검색 (거래점)
                    if (transaction.transOffice &&
                        transaction.transOffice.toLowerCase().includes(searchTerm)) {
                        return true;
                    }

                    // 거래번호 검색
                    if (transaction.transRefKey &&
                        transaction.transRefKey.toLowerCase().includes(searchTerm)) {
                        return true;
                    }

                    // 거래메모 검색
                    if (transaction.transactionMemo &&
                        transaction.transactionMemo.toLowerCase().includes(searchTerm)) {
                        return true;
                    }

                    // 거래유형 검색
                    if (transaction.transType &&
                        transaction.transType.toLowerCase().includes(searchTerm)) {
                        return true;
                    }

                    return false;
                });
            }

            // 거래 유형 필터 적용
            if (this.bankTransactionFilter !== 'all') {
                filtered = filtered.filter(transaction => {
                    if (this.bankTransactionFilter === 'deposit') {
                        // 입금만: depositAmount > 0
                        return parseFloat(transaction.depositAmount || 0) > 0;
                    } else if (this.bankTransactionFilter === 'withdrawal') {
                        // 출금만: withdrawalAmount > 0
                        return parseFloat(transaction.withdrawalAmount || 0) > 0;
                    }
                    return true;
                });
            }

            // 날짜 범위 필터 적용
            if (this.bankTransactionDateRange.startDate && this.bankTransactionDateRange.endDate) {
                const startDate = new Date(this.bankTransactionDateRange.startDate);
                const endDate = new Date(this.bankTransactionDateRange.endDate);

                filtered = filtered.filter(transaction => {
                    // YYYYMMDDHHMMSS 형식을 Date 객체로 변환
                    const transDate = this.parseTransactionDate(transaction.transactionDate);
                    if (!transDate) return false;

                    return transDate >= startDate && transDate <= endDate;
                });
            }

            return filtered;
        },
        // 카드 사용내역 필터링
        filteredCardTransactions() {
            let filtered = this.cardTransactions;

            // 거래 유형 필터 적용
            if (this.cardTransactionFilter !== 'all') {
                filtered = filtered.filter(transaction => {
                    if (this.cardTransactionFilter === 'purchase') {
                        return transaction.transactionType === 'purchase';
                    } else if (this.cardTransactionFilter === 'refund') {
                        return transaction.transactionType === 'refund';
                    }
                    return true;
                });
            }

            // 날짜 범위 필터 적용
            if (this.cardTransactionDateRange.startDate && this.cardTransactionDateRange.endDate) {
                const startDate = new Date(this.cardTransactionDateRange.startDate);
                const endDate = new Date(this.cardTransactionDateRange.endDate);

                filtered = filtered.filter(transaction => {
                    const useDate = this.parseCardTransactionDate(transaction.useDate);
                    if (!useDate) return false;
                    return useDate >= startDate && useDate <= endDate;
                });
            }

            return filtered;
        },
    },
    methods: {

        // 카드 사용내역 댓글 편집/작성
        async editCardTransactionComment(transaction) {
            try {
                // 기존 댓글 목록 표시
                if (transaction.comments && transaction.comments.length > 0) {
                    await this.showCardCommentList(transaction);
                } else {
                    // 새 댓글 작성
                    await this.addNewCardComment(transaction);
                }
            } catch (error) {
                console.error('카드 댓글 편집 중 오류:', error);
            }
        },

        // =========================================
        // 댓글 관련 메서드
        // =========================================
        // 댓글 표시 텍스트 생성
        getCommentDisplayText(transaction) {
            // comments 배열이 존재하고 유효한지 확인
            if (!transaction || !transaction.comments || !Array.isArray(transaction.comments) || transaction.comments.length === 0) {
                return '댓글 작성';
            }

            const commentCount = transaction.comments.length;
            if (commentCount === 1) {
                return '댓글 1개';
            } else {
                return `댓글 ${commentCount}개`;
            }
        },

        // 거래내역 댓글 편집/작성
        async editTransactionComment(transaction) {
            try {
                // comments 배열이 존재하고 유효한지 확인
                if (!transaction.comments || !Array.isArray(transaction.comments)) {
                    transaction.comments = [];
                }

                // 기존 댓글 목록 표시
                if (transaction.comments.length > 0) {
                    await this.showCommentList(transaction);
                } else {
                    // 새 댓글 작성
                    await this.addNewComment(transaction);
                }
            } catch (error) {
                console.error('댓글 편집 중 오류:', error);
            }
        },

        // 댓글 목록 표시 및 관리
        async showCommentList(transaction) {
            try {
                // comments 배열이 존재하고 유효한지 한 번 더 확인
                if (!transaction || !transaction.comments || !Array.isArray(transaction.comments) || transaction.comments.length === 0) {
                    console.log('댓글 데이터가 유효하지 않음:', transaction);
                    return;
                }

                // 댓글 모달 표시
                this.showCommentModal = true;
                this.currentTransaction = transaction;
            } catch (error) {
                console.error('댓글 목록 표시 중 오류:', error);
            }
        },

        // 댓글 모달 이벤트 핸들러
        handleCommentModalClose() {
            this.showCommentModal = false;
            this.currentTransaction = null;
        },

        async handleCommentSave({ index, content }) {
            try {
                const transaction = this.currentTransaction;
                if (!transaction || !transaction.comments[index]) return;

                this.$swal.fire({
                    title: '댓글 수정 중...',
                    allowOutsideClick: false,
                    didOpen: () => {
                        this.$swal.showLoading();
                    }
                });

                // 백엔드 API를 통해 댓글 수정
                const response = await this.$axios.put(
                    `${this.$apiUrls.hanqmedi}/payment/updateTransactionComment`,
                    {
                        commentId: transaction.comments[index].num || transaction.comments[index].id,
                        newContent: content
                    }
                );

                if (response.data.success) {
                    // 로컬 데이터 업데이트
                    transaction.comments[index].comment = content;
                    transaction.comments[index].updated_at = new Date().toISOString();

                    this.$swal.fire({
                        icon: 'success',
                        title: '댓글 수정 완료',
                        text: '댓글이 성공적으로 수정되었습니다.',
                        timer: 2000,
                        showConfirmButton: false
                    });
                } else {
                    throw new Error(response.data.message || '댓글 수정에 실패했습니다.');
                }
            } catch (error) {
                console.error('댓글 수정 실패:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '댓글 수정 실패',
                    text: '댓글 수정 중 오류가 발생했습니다.',
                    confirmButtonText: '확인'
                });
            }
        },

        async handleCommentDelete({ index }) {
            try {
                const transaction = this.currentTransaction;
                if (!transaction || !transaction.comments[index]) return;

                this.$swal.fire({
                    title: '댓글 삭제 중...',
                    allowOutsideClick: false,
                    didOpen: () => {
                        this.$swal.showLoading();
                    }
                });

                // 백엔드 API를 통해 댓글 삭제
                const commentId = transaction.comments[index].num || transaction.comments[index].id;
                const response = await this.$axios.delete(
                    `${this.$apiUrls.hanqmedi}/payment/deleteTransactionComment/${commentId}`
                );

                if (response.data.success) {
                    // 로컬 데이터에서 댓글 제거
                    transaction.comments.splice(index, 1);

                    this.$swal.fire({
                        icon: 'success',
                        title: '댓글 삭제 완료',
                        text: '댓글이 성공적으로 삭제되었습니다.',
                        timer: 2000,
                        showConfirmButton: false
                    });
                } else {
                    throw new Error(response.data.message || '댓글 삭제에 실패했습니다.');
                }
            } catch (error) {
                console.error('댓글 삭제 실패:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '댓글 삭제 실패',
                    text: '댓글 삭제 중 오류가 발생했습니다.',
                    confirmButtonText: '확인'
                });
            }
        },

        // 새 댓글 추가
        async addNewComment(transaction) {
            try {
                // comments 배열이 존재하지 않으면 초기화
                if (!transaction.comments || !Array.isArray(transaction.comments)) {
                    transaction.comments = [];
                }

                const { value: commentContent } = await this.$swal.fire({
                    title: '댓글 작성',
                    input: 'textarea',
                    inputPlaceholder: '거래내역에 대한 댓글을 입력하세요...',
                    inputAttributes: {
                        'aria-label': '댓글 입력',
                        'maxlength': '500',
                        'rows': '4'
                    },
                    showCancelButton: true,
                    confirmButtonText: '저장',
                    cancelButtonText: '취소',
                    inputValidator: (value) => {
                        if (!value || value.trim().length === 0) {
                            return '댓글 내용을 입력해주세요.';
                        }
                        if (value.length > 500) {
                            return '댓글은 500자 이내로 입력해주세요.';
                        }
                        return null;
                    }
                });

                if (commentContent && commentContent.trim()) {
                    this.$swal.fire({
                        title: '댓글 저장 중...',
                        allowOutsideClick: false,
                        didOpen: () => {
                            this.$swal.showLoading();
                        }
                    });

                    try {
                        // 백엔드 API를 통해 댓글 저장
                        const response = await this.$axios.post(
                            `${this.$apiUrls.hanqmedi}/payment/saveTransactionComment`,
                            {
                                transactionData: {
                                    transactionDate: transaction.transactionDate,
                                    withdrawalAmount: transaction.withdrawalAmount,
                                    depositAmount: transaction.depositAmount,
                                    balance: transaction.balance,
                                    transRemark1: transaction.transRemark1,
                                    transOffice: transaction.transOffice,
                                    transRefKey: transaction.transRefKey,
                                    transactionMemo: transaction.transactionMemo,
                                    comment: commentContent.trim()
                                }
                            }
                        );

                        if (response.data.success) {
                            // 로컬 데이터 업데이트
                            const newComment = {
                                id: response.data.data.id,
                                content: commentContent.trim(),
                                createdAt: new Date().toISOString(),
                                updatedAt: new Date().toISOString()
                            };

                            transaction.comments.push(newComment);

                            this.$swal.fire({
                                icon: 'success',
                                title: '댓글 저장 완료',
                                text: '새 댓글이 성공적으로 저장되었습니다.',
                                timer: 2000,
                                showConfirmButton: false
                            });
                        } else {
                            throw new Error(response.data.message || '댓글 저장에 실패했습니다.');
                        }
                    } catch (error) {
                        console.error('댓글 저장 실패:', error);

                        let errorMessage = '댓글 저장 중 오류가 발생했습니다.';
                        if (error.response?.data?.message) {
                            errorMessage = error.response.data.message;
                        } else if (error.message) {
                            errorMessage = error.message;
                        }

                        this.$swal.fire({
                            icon: 'error',
                            title: '댓글 저장 실패',
                            text: errorMessage,
                            confirmButtonText: '확인'
                        });
                    }
                }
            } catch (error) {
                console.error('새 댓글 추가 중 오류:', error);
            }
        },

        // 기존 댓글 수정/삭제
        async editExistingComment(transaction) {
            try {
                // 댓글 배열이 존재하고 비어있지 않은지 더 엄격하게 체크
                if (!transaction || !transaction.comments || !Array.isArray(transaction.comments) || transaction.comments.length === 0) {
                    console.log('댓글 데이터 상태:', {
                        transaction: !!transaction,
                        hasComments: !!(transaction && transaction.comments),
                        isArray: Array.isArray(transaction?.comments),
                        length: transaction?.comments?.length
                    });
                    return;
                }

                // 댓글 선택 옵션 생성
                const commentOptions = {};
                transaction.comments.forEach((comment, index) => {
                    if (comment && comment.comment) {
                        const displayText = comment.comment.length > 30
                            ? `${comment.comment.substring(0, 30)}...`
                            : comment.comment;
                        commentOptions[index] = `${index + 1}. ${displayText}`;
                    }
                });

                const { value: selectedCommentIndex } = await this.$swal.fire({
                    title: '수정/삭제할 댓글 선택',
                    input: 'select',
                    inputOptions: commentOptions,
                    inputPlaceholder: '댓글을 선택하세요',
                    showCancelButton: true,
                    confirmButtonText: '선택',
                    cancelButtonText: '취소'
                });

                if (selectedCommentIndex !== undefined) {
                    const commentIndex = parseInt(selectedCommentIndex);
                    const selectedComment = transaction.comments[commentIndex];

                    // 선택된 댓글이 유효한지 확인
                    if (!selectedComment) {
                        console.error('선택된 댓글이 유효하지 않음:', selectedComment);
                        return;
                    }

                    // 수정/삭제 선택
                    const { value: action } = await this.$swal.fire({
                        title: '댓글 관리',
                        text: '선택한 댓글을 어떻게 하시겠습니까?',
                        showCancelButton: true,
                        showDenyButton: true,
                        confirmButtonText: '수정',
                        denyButtonText: '삭제',
                        cancelButtonText: '취소',
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                        preConfirm: () => {
                            return 'edit';
                        },
                        preDeny: () => {
                            return 'delete';
                        }
                    });

                    if (action === 'edit') {
                        // 댓글 수정
                        await this.updateComment(transaction, selectedComment, commentIndex);
                    } else if (action === 'delete') {
                        // 댓글 삭제
                        await this.deleteComment(transaction, selectedComment, commentIndex);
                    }
                }
            } catch (error) {
                console.error('기존 댓글 수정/삭제 중 오류:', error);
                console.error('트랜잭션 데이터:', transaction);
            }
        },

        // 댓글 수정
        async updateComment(transaction, comment, commentIndex) {
            try {
                const { value: newContent } = await this.$swal.fire({
                    title: '댓글 수정',
                    input: 'textarea',
                    inputPlaceholder: '댓글 내용을 입력하세요...',
                    inputValue: comment.comment || comment.content || '',
                    inputAttributes: {
                        'aria-label': '댓글 수정',
                        'maxlength': '500',
                        'rows': '4'
                    },
                    showCancelButton: true,
                    confirmButtonText: '수정',
                    cancelButtonText: '취소',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    inputValidator: (value) => {
                        if (!value || value.trim().length === 0) {
                            return '댓글 내용을 입력해주세요.';
                        }
                        if (value.length > 500) {
                            return '댓글은 500자 이내로 입력해주세요.';
                        }
                        return null;
                    }
                });

                if (newContent && newContent.trim() && newContent !== (comment.comment || comment.content)) {
                    this.$swal.fire({
                        title: '댓글 수정 중...',
                        allowOutsideClick: false,
                        didOpen: () => {
                            this.$swal.showLoading();
                        }
                    });

                    try {
                        // 백엔드 API를 통해 댓글 수정
                        const response = await this.$axios.put(
                            `${this.$apiUrls.hanqmedi}/payment/updateTransactionComment`,
                            {
                                commentId: comment.num || comment.id,
                                newContent: newContent.trim()
                            }
                        );

                        if (response.data.success) {
                            // 로컬 데이터 업데이트
                            comment.comment = newContent.trim();
                            comment.updated_at = new Date().toISOString();

                            this.$swal.fire({
                                icon: 'success',
                                title: '댓글 수정 완료',
                                text: '댓글이 성공적으로 수정되었습니다.',
                                timer: 2000,
                                showConfirmButton: false
                            });
                        } else {
                            throw new Error(response.data.message || '댓글 수정에 실패했습니다.');
                        }
                    } catch (error) {
                        console.error('댓글 수정 실패:', error);

                        let errorMessage = '댓글 수정 중 오류가 발생했습니다.';
                        if (error.response?.data?.message) {
                            errorMessage = error.response.data.message;
                        } else if (error.message) {
                            errorMessage = error.message;
                        }

                        this.$swal.fire({
                            icon: 'error',
                            title: '댓글 수정 실패',
                            text: errorMessage,
                            confirmButtonText: '확인'
                        });
                    }
                }
            } catch (error) {
                console.error('댓글 수정 중 오류:', error);
            }
        },

        // 댓글 삭제
        async deleteComment(transaction, comment, commentIndex) {
            try {
                const { value: action } = await this.$swal.fire({
                    title: '댓글 삭제 확인',
                    text: '정말로 이 댓글을 삭제하시겠습니까?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: '삭제',
                    cancelButtonText: '취소',
                    confirmButtonColor: '#dc2626',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    preConfirm: () => {
                        return 'delete';
                    }
                });

                if (action === 'delete') {
                    this.$swal.fire({
                        title: '댓글 삭제 중...',
                        allowOutsideClick: false,
                        didOpen: () => {
                            this.$swal.showLoading();
                        }
                    });

                    try {
                        // 백엔드 API를 통해 댓글 삭제
                        // comment.num이 실제 댓글 ID (content가 아님)
                        const commentId = comment.num || comment.id || comment.comment_id || comment.commentId;

                        if (!commentId) {
                            throw new Error('댓글 ID를 찾을 수 없습니다.');
                        }

                        console.log('삭제할 댓글 ID:', commentId, '댓글 객체:', comment);

                        const response = await this.$axios.delete(
                            `${this.$apiUrls.hanqmedi}/payment/deleteTransactionComment/${commentId}`
                        );

                        if (response.data.success) {
                            // 로컬 데이터에서 댓글 제거
                            transaction.comments.splice(commentIndex, 1);

                            this.$swal.fire({
                                icon: 'success',
                                title: '댓글 삭제 완료',
                                text: '댓글이 성공적으로 삭제되었습니다.',
                                timer: 2000,
                                showConfirmButton: false
                            });
                        } else {
                            throw new Error(response.data.message || '댓글 삭제에 실패했습니다.');
                        }
                    } catch (error) {
                        console.error('댓글 삭제 실패:', error);

                        let errorMessage = '댓글 삭제 중 오류가 발생했습니다.';
                        if (error.response?.data?.message) {
                            errorMessage = error.response.data.message;
                        } else if (error.message) {
                            errorMessage = error.message;
                        }

                        this.$swal.fire({
                            icon: 'error',
                            title: '댓글 삭제 실패',
                            text: errorMessage,
                            confirmButtonText: '확인'
                        });
                    }
                }
            } catch (error) {
                console.error('댓글 삭제 중 오류:', error);
            }
        },

        // 댓글 날짜 포맷팅
        formatCommentDate(dateString) {
            if (!dateString) return '';

            try {
                const date = new Date(dateString);
                if (isNaN(date.getTime())) return dateString;

                const now = new Date();
                const diffTime = Math.abs(now - date);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                if (diffDays === 1) {
                    return '오늘';
                } else if (diffDays === 2) {
                    return '어제';
                } else if (diffDays <= 7) {
                    return `${diffDays - 1}일 전`;
                } else {
                    return date.toLocaleDateString('ko-KR');
                }
            } catch (error) {
                return dateString;
            }
        },

        handleSearchSubmit(event) {
            event.preventDefault();

            // 검색어가 비어있으면 모든 거래내역 표시
            if (!this.searchForm.name.trim()) {
                this.bankTransactionFilter = 'all';
                return;
            }

            // 검색어를 소문자로 변환하여 대소문자 구분 없이 검색
            const searchTerm = this.searchForm.name.trim().toLowerCase();

            // 현재 필터링된 거래내역에서 검색
            const searchResults = this.bankTransactions.filter(transaction => {
                // 거래내용 검색
                if (transaction.transRemark1 &&
                    transaction.transRemark1.toLowerCase().includes(searchTerm)) {
                    return true;
                }

                // 거래기록사항 검색 (거래점)
                if (transaction.transOffice &&
                    transaction.transOffice.toLowerCase().includes(searchTerm)) {
                    return true;
                }

                // 거래번호 검색
                if (transaction.transRefKey &&
                    transaction.transRefKey.toLowerCase().includes(searchTerm)) {
                    return true;
                }

                // 거래메모 검색
                if (transaction.transactionMemo &&
                    transaction.transactionMemo.toLowerCase().includes(searchTerm)) {
                    return true;
                }

                // 거래유형 검색
                if (transaction.transType &&
                    transaction.transType.toLowerCase().includes(searchTerm)) {
                    return true;
                }

                return false;
            });

            // 검색 결과를 필터링된 거래내역에 저장
            this.filteredBankTransactions = searchResults;

            // 검색 결과 알림
            if (searchResults.length > 0) {
                this.$swal.fire({
                    icon: 'success',
                    title: '검색 완료',
                    text: `검색어 "${this.searchForm.name.trim()}"에 대한 결과 ${searchResults.length}건을 찾았습니다.`,
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                this.$swal.fire({
                    icon: 'info',
                    title: '검색 결과 없음',
                    text: `검색어 "${this.searchForm.name.trim()}"에 대한 결과를 찾을 수 없습니다.`,
                    confirmButtonText: '확인'
                });
            }
        },
        handleCardSearchSubmit(event) {
            event.preventDefault();
            // 카드 검색 로직
        },

        // =========================================
        // 계좌관리 팝업 관련 메서드
        // =========================================
        async GetBankAccountManagementURL() {
            if (this.$globalState.barobill_id === '') {
                this.$swal.fire({
                    icon: 'info',
                    title: '계좌관리 팝업 불가능',
                    html: '바로빌 회원 아이디가 설정되지 않았습니다. <br>관리자에게 문의하세요.',
                    confirmButtonText: '확인'
                });
                return;
            }

            try {
                // 로딩 표시
                this.$swalLoading.show('계좌관리 팝업 준비 중...');

                // 백엔드 API 호출  
                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/barobill/GetBankAccountManagementURL`,
                    {
                        params: {
                            companykey: this.$globalState.company_key,
                            userId: this.$globalState.barobill_id
                        }
                    }
                );

                if (response.data.success) {
                    const popupUrl = response.data.data.url;

                    // 팝업 창 설정
                    const popupFeatures = [
                        'width=1200',
                        'height=800',
                        'scrollbars=yes',
                        'resizable=yes',
                        'toolbar=no',
                        'menubar=no',
                        'location=no',
                        'status=no',
                        'directories=no'
                    ].join(',');

                    // 팝업 창 열기
                    const popup = window.open(
                        popupUrl,
                        'BankAccountManagement',
                        popupFeatures
                    );

                    if (!popup) {
                        throw new Error('팝업 차단이 감지되었습니다. 팝업 차단을 해제해주세요.');
                    }

                    this.$swal.fire({
                        icon: 'success',
                        title: '팝업 준비 완료',
                        text: '계좌관리 팝업이 열렸습니다.',
                        timer: 2000,
                        showConfirmButton: false
                    });

                } else {
                    throw new Error(response.data.message || '계좌관리 팝업 URL 생성에 실패했습니다.');
                }

            } catch (error) {
                console.error('계좌관리 팝업 오류:', error);

                let errorMessage = '계좌관리 팝업을 열 수 없습니다.';

                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.$swal.fire({
                    icon: 'error',
                    title: '팝업 열기 실패',
                    text: errorMessage,
                    confirmButtonText: '확인',
                    showCloseButton: true
                });
            }
        },

        // =========================================
        // 카드 관련 메서드
        // =========================================
        async GetCardManagementURL() {
            try {
                this.$swalLoading.show('카드관리 팝업 준비 중...');

                // 백엔드 API 호출 - barobill/GetCardManagementURL 사용
                const response = await this.$axios.get(
                    `${this.$apiUrls.hanqmedi}/barobill/GetCardManagementURL`,
                    {
                        params: {
                            userId: this.$globalState.barobill_id
                        }
                    }
                );

                if (response.data.success) {
                    const popupUrl = response.data.data.url;
                    const popupFeatures = [
                        'width=1200',
                        'height=800',
                        'scrollbars=yes',
                        'resizable=yes',
                        'toolbar=no',
                        'menubar=no',
                        'location=no',
                        'status=no',
                        'directories=no'
                    ].join(',');

                    const popup = window.open(
                        popupUrl,
                        'CardManagement',
                        popupFeatures
                    );

                    if (!popup) {
                        throw new Error('팝업 차단이 감지되었습니다. 팝업 차단을 해제해주세요.');
                    }

                    this.$swal.fire({
                        icon: 'success',
                        title: '팝업 준비 완료',
                        text: '카드관리 팝업이 열렸습니다.',
                        timer: 2000,
                        showConfirmButton: false
                    });

                } else {
                    throw new Error(response.data.message || '카드관리 팝업 URL 생성에 실패했습니다.');
                }

            } catch (error) {
                console.error('카드관리 팝업 오류:', error);

                let errorMessage = '카드관리 팝업을 열 수 없습니다.';
                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.$swal.fire({
                    icon: 'error',
                    title: '팝업 열기 실패',
                    text: errorMessage,
                    confirmButtonText: '확인'
                });
            }
        },

        // 카드 사용내역 조회
        async loadCardTransactions() {
            try {
                this.$swalLoading.show('카드 사용내역 조회 중...');

                // 날짜 형식을 YYYYMMDD로 변환
                const startDate = this.cardTransactionDateRange.startDate.replace(/-/g, '');
                const endDate = this.cardTransactionDateRange.endDate.replace(/-/g, '');

                // 바로빌 API를 통해 카드 사용내역 조회
                const response = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/barobill/GetPeriodCardApprovalLog`,
                    {
                        id: this.$globalState.barobill_id,
                        cardNum: '1234567890123456',
                        startDate: startDate,
                        endDate: endDate,
                        countPerPage: 100,
                        currentPage: 1,
                        orderDirection: 2
                    }
                );

                if (response.data.success) {
                    // 백엔드에서 받은 데이터를 프론트엔드 형식으로 변환
                    const backendData = response.data.data;

                    // approvals 배열이 어디에 있는지 확인
                    let approvals = [];
                    if (backendData.approvals) {
                        approvals = backendData.approvals;
                    } else if (backendData.data && backendData.data.approvals) {
                        approvals = backendData.data.approvals;
                    } else if (Array.isArray(backendData)) {
                        approvals = backendData;
                    } else {
                        approvals = [];
                    }

                    this.cardTransactions = this.transformCardBackendData(approvals);

                    // 각 카드 사용내역에 대해 기존 댓글 조회
                    await this.loadExistingCommentsForCardTransactions();

                    this.$swal.fire({
                        icon: 'success',
                        title: '조회 완료',
                        text: `${this.cardTransactions.length}건의 카드 사용내역을 조회했습니다.`,
                        confirmButtonText: '확인'
                    });
                } else {
                    throw new Error(response.data.message || '카드 사용내역 조회에 실패했습니다.');
                }
            } catch (error) {
                console.error('카드 사용내역 조회 실패:', error);

                let errorMessage = '카드 사용내역 조회 중 오류가 발생했습니다.';
                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.$swal.fire({
                    icon: 'error',
                    title: '조회 실패',
                    text: errorMessage,
                    confirmButtonText: '확인'
                });
            }
        },

        // 카드 사용내역에 대한 기존 댓글 조회
        async loadExistingCommentsForCardTransactions() {
            try {
                if (!this.cardTransactions || this.cardTransactions.length === 0) {
                    return;
                }

                // 각 카드 사용내역에 대해 댓글 조회
                for (const transaction of this.cardTransactions) {
                    await this.loadCommentsForCardTransaction(transaction);
                }
            } catch (error) {
                console.error('카드 기존 댓글 조회 실패:', error);
            }
        },

        // 특정 카드 사용내역에 대한 댓글 조회
        async loadCommentsForCardTransaction(transaction) {
            try {
                // 승인번호, 카드번호, 가맹점명을 기준으로 댓글 검색
                const searchCriteria = {
                    approvalNumber: transaction.approvalNumber,
                    cardNumber: transaction.cardNumber,
                    merchantName: transaction.merchantName
                };

                const response = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/payment/searchCardTransactionComments`,
                    { searchCriteria }
                );

                if (response.data.success) {
                    // 댓글 데이터를 카드 사용내역에 추가
                    transaction.comments = response.data.data.comments || [];
                } else {
                    transaction.comments = [];
                }
            } catch (error) {
                console.error('카드 사용내역 댓글 조회 실패:', error);
                transaction.comments = [];
            }
        },

        // 카드 메모 편집/작성
        async editCardTransactionMemo(transaction) {
            try {
                const { value: memo } = await this.$swal.fire({
                    title: '카드 사용내역 메모 작성',
                    input: 'textarea',
                    inputPlaceholder: '카드 사용내역에 대한 메모를 입력하세요...',
                    inputValue: transaction.memo || '',
                    inputAttributes: {
                        'aria-label': '카드 메모 입력',
                        'maxlength': '200',
                        'rows': '4'
                    },
                    showCancelButton: true,
                    confirmButtonText: '저장',
                    cancelButtonText: '취소',
                    inputValidator: (value) => {
                        if (value && value.length > 200) {
                            return '메모는 200자 이내로 입력해주세요.';
                        }
                        return null;
                    }
                });

                if (memo !== undefined) {
                    this.$swal.fire({
                        title: '메모 저장 중...',
                        allowOutsideClick: false,
                        didOpen: () => {
                            this.$swal.showLoading();
                        }
                    });

                    try {
                        // 바로빌 API를 통해 카드 메모 등록 - barobill/RegistCardLogMemo 사용
                        const response = await this.$axios.post(
                            `${this.$apiUrls.hanqmedi}/barobill/RegistCardLogMemo`,
                            {
                                cardNum: transaction.cardNumber || '1234567890123456',
                                useKey: transaction.useKey || transaction.approvalNumber,
                                memo: memo || ''
                            }
                        );

                        if (response.data.success) {
                            transaction.memo = memo;

                            this.$swal.fire({
                                icon: 'success',
                                title: '메모 저장 완료',
                                text: '카드 사용내역 메모가 성공적으로 저장되었습니다.',
                                timer: 2000,
                                showConfirmButton: false
                            });
                        } else {
                            throw new Error(response.data.message || '메모 저장에 실패했습니다.');
                        }
                    } catch (error) {
                        console.error('카드 메모 저장 실패:', error);

                        let errorMessage = '카드 메모 저장 중 오류가 발생했습니다.';
                        if (error.response?.data?.message) {
                            errorMessage = error.response.data.message;
                        } else if (error.message) {
                            errorMessage = error.message;
                        }

                        this.$swal.fire({
                            icon: 'error',
                            title: '메모 저장 실패',
                            text: errorMessage,
                            confirmButtonText: '확인'
                        });
                    }
                }
            } catch (error) {
                console.error('카드 메모 편집 중 오류:', error);
            }
        },
        // 카드 사용내역 조회
        async loadCardTransactions() {
            try {
                this.$swalLoading.show('카드 사용내역 조회 중...');

                // 카드 사용내역 조회 API 호출 (백엔드 구현 필요)
                const response = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/barobill/GetPeriodCardApprovalLog`,
                    {
                        id: this.$globalState.barobill_id,
                        cardNum: '1234567890123456',
                        startDate: this.cardTransactionDateRange.startDate.replace(/-/g, ''),
                        endDate: this.cardTransactionDateRange.endDate.replace(/-/g, ''),
                        countPerPage: 100,
                        currentPage: 1,
                        orderDirection: 2
                    }
                );

                if (response.data.success) {
                    this.cardTransactions = this.transformCardBackendData(response.data.data);

                    this.$swal.fire({
                        icon: 'success',
                        title: '조회 완료',
                        text: `${this.cardTransactions.length}건의 카드 사용내역을 조회했습니다.`,
                        confirmButtonText: '확인'
                    });
                } else {
                    throw new Error(response.data.message || '카드 사용내역 조회에 실패했습니다.');
                }
            } catch (error) {
                console.error('카드 사용내역 조회 실패:', error);

                let errorMessage = '카드 사용내역 조회 중 오류가 발생했습니다.';
                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.$swal.fire({
                    icon: 'error',
                    title: '조회 실패',
                    text: errorMessage,
                    confirmButtonText: '확인'
                });
            }
        },

        // 카드 메모 편집/작성
        async editCardTransactionMemo(transaction) {
            try {
                const { value: memo } = await this.$swal.fire({
                    title: '카드 사용내역 메모 작성',
                    input: 'textarea',
                    inputPlaceholder: '카드 사용내역에 대한 메모를 입력하세요...',
                    inputValue: transaction.memo || '',
                    inputAttributes: {
                        'aria-label': '카드 메모 입력',
                        'maxlength': '200',
                        'rows': '4'
                    },
                    showCancelButton: true,
                    confirmButtonText: '저장',
                    cancelButtonText: '취소',
                    inputValidator: (value) => {
                        if (value && value.length > 200) {
                            return '메모는 200자 이내로 입력해주세요.';
                        }
                        return null;
                    }
                });

                if (memo !== undefined) {
                    this.$swal.fire({
                        title: '메모 저장 중...',
                        allowOutsideClick: false,
                        didOpen: () => {
                            this.$swal.showLoading();
                        }
                    });

                    try {
                        // 카드 메모 등록 API 호출 (백엔드 구현 필요)
                        const response = await this.$axios.post(
                            `${this.$apiUrls.hanqmedi}/card/RegistCardTransactionMemo`,
                            {
                                cardNumber: transaction.cardNumber,
                                approvalNumber: transaction.approvalNumber,
                                memo: memo || ''
                            }
                        );

                        if (response.data.success) {
                            transaction.memo = memo;

                            this.$swal.fire({
                                icon: 'success',
                                title: '메모 저장 완료',
                                text: '카드 사용내역 메모가 성공적으로 저장되었습니다.',
                                timer: 2000,
                                showConfirmButton: false
                            });
                        } else {
                            throw new Error(response.data.message || '메모 저장에 실패했습니다.');
                        }
                    } catch (error) {
                        console.error('카드 메모 저장 실패:', error);

                        let errorMessage = '카드 메모 저장 중 오류가 발생했습니다.';
                        if (error.response?.data?.message) {
                            errorMessage = error.response.data.message;
                        } else if (error.message) {
                            errorMessage = error.message;
                        }

                        this.$swal.fire({
                            icon: 'error',
                            title: '메모 저장 실패',
                            text: errorMessage,
                            confirmButtonText: '확인'
                        });
                    }
                }
            } catch (error) {
                console.error('카드 메모 편집 중 오류:', error);
            }
        },

        // =========================================
        // 입출금내역 관련 메서드 (ClaimPage에서 가져옴)
        // =========================================

        // YYYYMMDDHHMMSS 형식의 날짜를 Date 객체로 파싱
        parseTransactionDate(dateString) {
            if (!dateString || dateString.length !== 14) return null;

            try {
                const year = parseInt(dateString.substring(0, 4));
                const month = parseInt(dateString.substring(4, 6)) - 1; // 월은 0부터 시작
                const day = parseInt(dateString.substring(6, 8));
                const hour = parseInt(dateString.substring(8, 10));
                const minute = parseInt(dateString.substring(10, 12));
                const second = parseInt(dateString.substring(12, 14));

                return new Date(year, month, day, hour, minute, second);
            } catch (error) {
                console.error('날짜 파싱 오류:', error, dateString);
                return null;
            }
        },

        // 입출금내역 조회
        async loadBankTransactions() {
            this.isLoadingBankTransactions = true;

            try {
                if (!this.$globalState.barobill_id ||
                    typeof this.$globalState.barobill_id === 'number' ||
                    !this.$globalState.barobill_account_no) {

                    try {
                        const barobillInfoResponse = await this.$axios.get(
                            `${this.$apiUrls.hanqmedi}/barobill/getBarobillInfo`,
                            {
                                params: {
                                    companykey: this.$globalState.company_key
                                }
                            }
                        );

                        if (barobillInfoResponse.data.success && barobillInfoResponse.data.data) {
                            const { barobillInfo, accountInfo } = barobillInfoResponse.data.data;

                            // 바로빌 회원 아이디 저장
                            this.$globalState.barobill_id = barobillInfo.bill_id;

                            // 계좌 정보 저장
                            if (accountInfo) {
                                this.$globalState.barobill_account_no = accountInfo.account_no;
                                this.$globalState.barobill_account_start_dt = accountInfo.start_dt;
                                this.$globalState.barobill_account_end_dt = accountInfo.end_dt;
                            } else {
                                console.warn('등록된 바로빌 계좌가 없습니다.');
                                this.$swal.fire({
                                    icon: 'warning',
                                    title: '계좌 정보 없음',
                                    text: '바로빌 계좌를 먼저 등록해주세요.',
                                    confirmButtonText: '확인'
                                });
                                return;
                            }
                        } else {
                            console.error('바로빌 정보 조회 실패:', barobillInfoResponse.data);
                            throw new Error(barobillInfoResponse.data.message || '바로빌 정보를 찾을 수 없습니다.');
                        }
                    } catch (error) {
                        console.error('바로빌 정보 로드 실패:', error);

                        if (error.response) {
                            console.error('에러 응답:', {
                                status: error.response.status,
                                data: error.response.data
                            });
                        }

                        this.$swal.fire({
                            icon: 'info',
                            title: '바로빌 정보 없음',
                            text: '시스템 관리에서 바로빌 정보를 등록해주세요.',
                            confirmButtonText: '확인'
                        });
                        return;
                    }
                }

                // 날짜 형식 결정: 계좌 등록일 기반 또는 사용자 선택 날짜
                let startDate, endDate;

                if (this.$globalState.barobill_account_start_dt) {
                    // 계좌 등록일을 기준으로 시작일 설정
                    const accountStartDate = new Date(this.$globalState.barobill_account_start_dt);
                    const accountStartDateStr = accountStartDate.toISOString().split('T')[0].replace(/-/g, '');

                    // 사용자가 선택한 날짜
                    const userStartDate = this.bankTransactionDateRange.startDate.replace(/-/g, '');
                    const userEndDate = this.bankTransactionDateRange.endDate.replace(/-/g, '');

                    // 계좌 등록일보다 이전 날짜는 조회할 수 없으므로 조정
                    startDate = userStartDate < accountStartDateStr ? accountStartDateStr : userStartDate;
                    endDate = userEndDate;
                } else {
                    // 계좌 등록일 정보가 없으면 사용자 선택 날짜 사용
                    startDate = this.bankTransactionDateRange.startDate.replace(/-/g, '');
                    endDate = this.bankTransactionDateRange.endDate.replace(/-/g, '');
                }

                // 바로빌 API를 통해 입출금내역 조회
                const response = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/barobill/GetPeriodBankAccountTransLog`,
                    {
                        id: this.$globalState.barobill_id, // 바로빌 회원 아이디 (문자열)
                        bankAccountNum: this.$globalState.barobill_account_no, // DB에서 조회한 계좌번호
                        startDate: startDate, // YYYYMMDD 형식
                        endDate: endDate, // YYYYMMDD 형식
                        transDirection: 1, // 1: 전체 조회
                        countPerPage: 100, // 페이지당 조회 건수
                        currentPage: 1, // 조회할 페이지 번호
                        orderDirection: 2 // 2: DESC (최신순)
                    }
                );

                if (response.data.success) {
                    // 백엔드에서 받은 데이터를 프론트엔드 형식으로 변환
                    const backendData = response.data.data;

                    // transactions 배열이 어디에 있는지 확인
                    let transactions = [];
                    if (backendData.transactions) {
                        transactions = backendData.transactions;
                    } else if (backendData.data && backendData.data.transactions) {
                        transactions = backendData.data.transactions;
                    } else if (Array.isArray(backendData)) {
                        transactions = backendData;
                    } else {
                        transactions = [];
                    }

                    this.bankTransactions = this.transformBackendData(transactions);

                    // 각 거래내역에 대해 기존 댓글 조회
                    await this.loadExistingCommentsForTransactions();

                } else {
                    throw new Error(response.data.message || '입출금내역 조회에 실패했습니다.');
                }
            } catch (error) {
                console.error('입출금내역 조회 실패:', error);

                let errorMessage = '입출금내역 조회 중 오류가 발생했습니다.';
                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.$swal.fire({
                    icon: 'error',
                    title: '조회 실패',
                    text: errorMessage,
                    confirmButtonText: '확인'
                });
            } finally {
                this.isLoadingBankTransactions = false;
            }
        },

        // 거래내역에 대한 기존 댓글 조회
        async loadExistingCommentsForTransactions() {
            try {
                if (!this.bankTransactions || this.bankTransactions.length === 0) {
                    return;
                }

                // 각 거래내역에 대해 댓글 조회
                for (const transaction of this.bankTransactions) {
                    await this.loadCommentsForTransaction(transaction);
                }
            } catch (error) {
                console.error('기존 댓글 조회 실패:', error);
            }
        },

        // 특정 거래내역에 대한 댓글 조회
        async loadCommentsForTransaction(transaction) {
            try {
                // 거래내용, 거래기록사항, 거래번호를 기준으로 댓글 검색
                const searchCriteria = {
                    transRemark1: transaction.transRemark1,
                    transOffice: transaction.transOffice,
                    transRefKey: transaction.transRefKey
                };

                const response = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/payment/searchTransactionComments`,
                    { searchCriteria }
                );

                if (response.data.success) {
                    // 댓글 데이터를 거래내역에 추가
                    transaction.comments = response.data.data.comments || [];
                } else {
                    transaction.comments = [];
                }
            } catch (error) {
                console.error('거래내역 댓글 조회 실패:', error);
                transaction.comments = [];
            }
        },

        // 거래메모 편집/작성
        async editTransactionMemo(transaction) {
            try {
                const { value: memo } = await this.$swal.fire({
                    title: '거래메모 작성',
                    input: 'textarea',
                    inputPlaceholder: '입출금 내역에 대한 메모를 입력하세요...',
                    inputValue: transaction.transactionMemo || '',
                    inputAttributes: {
                        'aria-label': '거래메모 입력',
                        'maxlength': '200',
                        'rows': '4'
                    },
                    showCancelButton: true,
                    confirmButtonText: '저장',
                    cancelButtonText: '취소',
                    inputValidator: (value) => {
                        if (value && value.length > 200) {
                            return '메모는 200자 이내로 입력해주세요.';
                        }
                        return null;
                    }
                });

                if (memo !== undefined) { // 취소가 아닌 경우
                    // 로딩 표시
                    this.$swal.fire({
                        title: '메모 저장 중...',
                        allowOutsideClick: false,
                        didOpen: () => {
                            this.$swal.showLoading();
                        }
                    });

                    try {
                        // 바로빌 API를 통해 메모 등록
                        const response = await this.$axios.post(
                            `${this.$apiUrls.hanqmedi}/barobill/RegistBankAccountLogMemo`,
                            {
                                bankAccountNum: transaction.bankAccountNum || '3021727065601',
                                transRefKey: transaction.transRefKey || transaction.transactionRefKey,
                                memo: memo || ''
                            }
                        );

                        if (response.data.success) {
                            // 로컬 데이터 업데이트
                            transaction.transactionMemo = memo;

                            this.$swal.fire({
                                icon: 'success',
                                title: '메모 저장 완료',
                                text: '거래메모가 성공적으로 저장되었습니다.',
                                timer: 2000,
                                showConfirmButton: false
                            });
                        } else {
                            throw new Error(response.data.message || '메모 저장에 실패했습니다.');
                        }
                    } catch (error) {
                        console.error('메모 저장 실패:', error);

                        let errorMessage = '메모 저장 중 오류가 발생했습니다.';
                        if (error.response?.data?.message) {
                            errorMessage = error.response.data.message;
                        } else if (error.message) {
                            errorMessage = error.message;
                        }

                        this.$swal.fire({
                            icon: 'error',
                            title: '메모 저장 실패',
                            text: errorMessage,
                            confirmButtonText: '확인'
                        });
                    }
                }
            } catch (error) {
                console.error('메모 편집 중 오류:', error);
            }
        },

        // 백엔드 데이터를 프론트엔드 형식으로 변환
        transformBackendData(backendTransactions) {
            if (!Array.isArray(backendTransactions)) {
                return [];
            }

            const transformed = backendTransactions.map((transaction, index) => {
                // 백엔드 응답 구조를 그대로 활용
                const transformedTransaction = {
                    transactionDate: transaction.TransDT || '', // 거래일시 (YYYYMMDDHHMMSS 형식)
                    withdrawalAmount: parseFloat(transaction.Withdraw || 0), // 출금금액
                    depositAmount: parseFloat(transaction.Deposit || 0), // 입금금액
                    balance: parseFloat(transaction.Balance || 0), // 잔액
                    transRemark1: transaction.TransRemark1 || '', // 거래내용
                    transOffice: transaction.TransOffice || '', // 거래점
                    transType: transaction.TransType || '', // 거래유형
                    transactionMemo: transaction.Memo || '', // 거래메모
                    transRefKey: transaction.TransRefKey || '', // 거래 참조 키 (메모 등록용)
                    bankAccountNum: transaction.BankAccountNum || '3021727065601' // 계좌번호 (메모 등록용)
                };

                return transformedTransaction;
            });

            return transformed;
        },

        // 빠른 날짜 선택 처리
        handleQuickDateSelect() {
            if (!this.quickDateSelect) return;

            const today = new Date();
            let startDate = new Date();

            switch (this.quickDateSelect) {
                case 'today':
                    // 당일: 오늘 하루
                    startDate = new Date(today);
                    break;
                case 'yesterday':
                    // 전일: 어제 하루
                    startDate.setDate(today.getDate() - 1);
                    break;
                case '3days':
                    // 3일: 3일 전부터
                    startDate.setDate(today.getDate() - 3);
                    break;
                case '1week':
                    // 1주: 7일 전부터
                    startDate.setDate(today.getDate() - 7);
                    break;
                case '15days':
                    // 15일: 15일 전부터
                    startDate.setDate(today.getDate() - 15);
                    break;
                case '1month':
                    // 1개월: 30일 전부터
                    startDate.setDate(today.getDate() - 30);
                    break;
                case '3months':
                    // 3개월: 90일 전부터
                    startDate.setDate(today.getDate() - 90);
                    break;
                case '6months':
                    // 6개월: 180일 전부터
                    startDate.setDate(today.getDate() - 180);
                    break;
            }

            // 날짜 범위 설정
            this.bankTransactionDateRange = {
                startDate: startDate.toISOString().split('T')[0],
                endDate: today.toISOString().split('T')[0]
            };

            // 빠른 선택 후 자동으로 입출금내역 조회
            this.loadBankTransactions();
        },

        // 입출금내역 필터 변경 처리
        handleBankTransactionFilterChange() {
            // 필터 변경 시 로깅
            console.log('입출금내역 필터 변경:', this.bankTransactionFilter);
            console.log('필터링된 결과:', this.filteredBankTransactions.length);
        },

        // 입출금내역 시작일 변경 시 빠른 선택만 초기화
        handleBankTransactionStartDateChange() {
            // 빠른 선택 초기화만 수행
            this.quickDateSelect = '';
        },

        // 입출금내역 종료일 변경 시 빠른 선택만 초기화
        handleBankTransactionEndDateChange() {
            // 빠른 선택 초기화만 수행
            this.quickDateSelect = '';
        },

        // 거래일시 포맷팅 (YYYYMMDDHHMMSS 형식 처리)
        formatTransactionDateTime(dateString) {
            if (!dateString) return '-';

            try {
                // YYYYMMDDHHMMSS 형식 (예: 20250816092209)
                if (dateString.length === 14 && /^\d{14}$/.test(dateString)) {
                    const year = dateString.substring(0, 4);
                    const month = dateString.substring(4, 6);
                    const day = dateString.substring(6, 8);
                    const hour = dateString.substring(8, 10);
                    const minute = dateString.substring(10, 12);
                    const second = dateString.substring(12, 14);

                    return `${year}.${month}.${day} ${hour}:${minute}`;
                }

                // 일반 날짜 형식인 경우
                const date = new Date(dateString);
                if (isNaN(date.getTime())) {
                    return dateString;
                }

                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');

                return `${year}.${month}.${day} ${hours}:${minutes}`;
            } catch (error) {
                console.error('거래일시 파싱 오류:', error, dateString);
                return dateString;
            }
        },

        // 입출금내역 날짜 범위 초기화
        initializeBankTransactionDateRange() {
            // 기본값: 이번 달 1일부터 오늘까지
            const today = new Date();
            const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

            this.bankTransactionDateRange = {
                startDate: firstDayOfMonth.toISOString().split('T')[0],
                endDate: today.toISOString().split('T')[0]
            };

            // 빠른 선택 초기화
            this.quickDateSelect = '';
        },

        // 카드 날짜 파싱
        parseCardTransactionDate(dateString) {
            if (!dateString) return null;

            try {
                const date = new Date(dateString);
                if (isNaN(date.getTime())) {
                    return null;
                }
                return date;
            } catch (error) {
                console.error('카드 날짜 파싱 오류:', error, dateString);
                return null;
            }
        },

        // 카드 사용내역 필터 변경 처리
        handleCardTransactionFilterChange() {
            console.log('카드 사용내역 필터 변경:', this.cardTransactionFilter);
            console.log('필터링된 결과:', this.filteredCardTransactions.length);
        },

        // 카드 빠른 날짜 선택 처리
        handleCardQuickDateSelect() {
            if (!this.cardQuickDateSelect) return;

            const today = new Date();
            let startDate = new Date();

            switch (this.cardQuickDateSelect) {
                case 'today':
                    startDate = new Date(today);
                    break;
                case 'yesterday':
                    startDate.setDate(today.getDate() - 1);
                    break;
                case '3days':
                    startDate.setDate(today.getDate() - 3);
                    break;
                case '1week':
                    startDate.setDate(today.getDate() - 7);
                    break;
                case '15days':
                    startDate.setDate(today.getDate() - 15);
                    break;
                case '1month':
                    startDate.setDate(today.getDate() - 30);
                    break;
                case '3months':
                    startDate.setDate(today.getDate() - 90);
                    break;
                case '6months':
                    startDate.setDate(today.getDate() - 180);
                    break;
            }

            this.cardTransactionDateRange = {
                startDate: startDate.toISOString().split('T')[0],
                endDate: today.toISOString().split('T')[0]
            };

            this.loadCardTransactions();
        },

        // 카드 입출금내역 시작일 변경 시 빠른 선택만 초기화
        handleCardTransactionStartDateChange() {
            this.cardQuickDateSelect = '';
        },

        // 카드 입출금내역 종료일 변경 시 빠른 선택만 초기화
        handleCardTransactionEndDateChange() {
            this.cardQuickDateSelect = '';
        },

        // 카드 사용일시 포맷팅
        formatCardTransactionDateTime(dateString) {
            if (!dateString) return '-';

            try {
                const date = new Date(dateString);
                if (isNaN(date.getTime())) {
                    return dateString;
                }

                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');

                return `${year}.${month}.${day} ${hours}:${minutes}`;
            } catch (error) {
                console.error('카드 사용일시 파싱 오류:', error, dateString);
                return dateString;
            }
        },

        // 카드 백엔드 데이터를 프론트엔드 형식으로 변환
        transformCardBackendData(backendTransactions) {
            if (!Array.isArray(backendTransactions)) {
                return [];
            }

            const transformed = backendTransactions.map((transaction, index) => {
                return {
                    useDate: transaction.UseDate || transaction.useDate || '',
                    useAmount: parseFloat(transaction.UseAmount || transaction.useAmount || 0),
                    installmentMonths: transaction.InstallmentMonths || transaction.installmentMonths || '',
                    approvalNumber: transaction.ApprovalNumber || transaction.approvalNumber || '',
                    merchantName: transaction.MerchantName || transaction.merchantName || '',
                    cardType: transaction.CardType || transaction.cardType || '',
                    cardNumber: transaction.CardNumber || transaction.cardNumber || '',
                    memo: transaction.Memo || transaction.memo || '',
                    transactionType: transaction.TransactionType || transaction.transactionType || 'purchase'
                };
            });

            return transformed;
        },

        // 카드 날짜 범위 초기화
        initializeCardTransactionDateRange() {
            const today = new Date();
            const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

            this.cardTransactionDateRange = {
                startDate: firstDayOfMonth.toISOString().split('T')[0],
                endDate: today.toISOString().split('T')[0]
            };

            this.cardQuickDateSelect = '';
        },

        // 검색어 초기화
        clearSearch() {
            this.searchForm.name = '';
            this.bankTransactionFilter = 'all';
        },

        formatPrice(price) {
            return new Intl.NumberFormat('ko-KR').format(price || 0);
        },

    },
    mounted() {
        this.initializeBankTransactionDateRange();
        this.initializeCardTransactionDateRange();
        this.loadBankTransactions();
        // this.loadCardTransactions();
    }
};
</script>

<style>
.blurred {
    filter: blur(4px) !important;
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}
</style>